import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@shared/ui';

interface SuspenseAssignDialogProps {
  open: boolean;
  loading?: boolean;
  invoiceId: string;
  reason: string;
  onOpenChange: (open: boolean) => void;
  onInvoiceIdChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
}

export function SuspenseAssignDialog({
  open,
  loading = false,
  invoiceId,
  reason,
  onOpenChange,
  onInvoiceIdChange,
  onReasonChange,
  onConfirm,
}: SuspenseAssignDialogProps) {
  const { t } = useTranslation('cabinet');
  const [error, setError] = useState<string | null>(null);
  const valid = invoiceId.trim().length > 0 && reason.trim().length >= 3;

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  const submit = () => {
    if (!valid) {
      setError(t('reconciliation.suspense.assignValidation'));
      return;
    }
    setError(null);
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('reconciliation.suspense.assignTitle')}</DialogTitle>
          <DialogDescription>{t('reconciliation.suspense.assignDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="suspense-invoice-id">{t('reconciliation.suspense.invoiceId')}</Label>
            <Input
              id="suspense-invoice-id"
              value={invoiceId}
              className="font-mono"
              onChange={(event) => onInvoiceIdChange(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="suspense-assign-reason">{t('reconciliation.enqueue.reason')}</Label>
            <Textarea
              id="suspense-assign-reason"
              value={reason}
              rows={3}
              onChange={(event) => onReasonChange(event.target.value)}
            />
          </div>
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            {t('withdrawals.actions.back')}
          </Button>
          <Button type="button" className="w-full sm:w-auto" loading={loading} disabled={!valid} onClick={submit}>
            {t('reconciliation.suspense.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
